// Humor social de una crisis: qué dice la gente en los comentarios de las
// publicaciones sobre el tema. Reemplaza a la vieja "línea de tiempo" del panel.
//
// Carga en diferido (su propio fetch) para no demorar la apertura del panel.
// "A favor / en contra" es en clave de bando: a favor = apoya a Chouhy o ataca
// al rival — es lo que importa medir en campaña, no el tono literal.

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';
import { FaHeart, FaExternalLinkAlt, FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import {
  TechPanel, PanelHeader, PanelTitle, TechChip, Empty, ErrorBox, Loading,
  mono, borderSoft,
} from '../../styles/techComponents';
import { getHumorSocial } from '../../services/crisisService';

const C = { pos: '#06d6a0', neg: '#ef476f', neu: '#94a3b8' };

const ejeStyle = { fontSize: 11, fill: 'rgba(255,255,255,0.45)' };
const tooltipStyle = {
  background: 'rgba(11,19,32,0.96)', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 8, fontSize: 12,
};
const diaCorto = (f) => new Date(`${String(f).slice(0, 10)}T12:00:00`)
  .toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });

const Lectura = styled.p`
  margin: 0 0 14px; font-size: 0.92rem; line-height: 1.6; color: var(--text-primary);
  padding-left: 10px; border-left: 2px solid ${(p) => p.$color || 'var(--btn-primary)'};
`;

const Grid = styled.div`
  display: grid; gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const Sub = styled.div`
  font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--text-secondary); font-family: ${mono}; margin-bottom: 10px;
`;

const Barra = styled.div`
  display: flex; height: 10px; border-radius: 5px; overflow: hidden; margin: 6px 0 4px;
  background: rgba(255,255,255,0.05);
`;
const Seg = styled.div`height: 100%; background: ${(p) => p.$c};`;

const Tema = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 6px 0;
  border-bottom: 1px solid ${borderSoft};
  &:last-child { border-bottom: none; }
  .n { flex: 1; font-size: 0.85rem; color: var(--text-primary); }
  .m { font-family: ${mono}; font-size: 0.78rem; color: var(--text-secondary); }
`;

const EmoBar = styled.div`
  display: grid; grid-template-columns: 90px 1fr 34px; align-items: center; gap: 8px;
  padding: 4px 0; font-size: 0.8rem;
  .l { color: var(--text-secondary); text-transform: capitalize; }
  .track { height: 7px; border-radius: 4px; background: rgba(255,255,255,0.06); overflow: hidden; }
  .fill { height: 100%; background: var(--btn-primary); }
  .v { font-family: ${mono}; font-size: 0.72rem; color: var(--text-secondary); text-align: right; }
`;

const Voz = styled.div`
  font-size: 0.84rem; line-height: 1.5; color: var(--text-secondary);
  padding: 8px 10px; margin-bottom: 6px; border-radius: 6px;
  border-left: 2px solid ${(p) => p.$c};
  background: rgba(255,255,255,0.02);
`;

const Post = styled.a`
  display: block; padding: 10px 0; border-bottom: 1px solid ${borderSoft};
  text-decoration: none; color: inherit;
  &:last-child { border-bottom: none; }
  &:hover .cap { color: var(--btn-primary); }
  .cap { font-size: 0.85rem; line-height: 1.45; color: var(--text-primary); }
  .meta { font-size: 0.74rem; color: var(--text-secondary); margin-top: 5px; display: flex; gap: 12px; flex-wrap: wrap; font-family: ${mono}; }
`;

export default function HumorSocial({ crisisId }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    getHumorSocial(crisisId)
      .then((d) => { if (vivo) setData(d); })
      .catch(() => { if (vivo) setError(true); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [crisisId]);

  if (cargando) return (
    <TechPanel style={{ gridColumn: '1 / -1' }}>
      <PanelHeader><PanelTitle>Humor social</PanelTitle></PanelHeader>
      <Loading>Analizando los comentarios…</Loading>
    </TechPanel>
  );

  if (error) return (
    <TechPanel style={{ gridColumn: '1 / -1' }}>
      <PanelHeader><PanelTitle>Humor social</PanelTitle></PanelHeader>
      <ErrorBox>No se pudo cargar el análisis de comentarios.</ErrorBox>
    </TechPanel>
  );

  if (!data?.disponible || !(data.totales?.pos + data.totales?.neg + data.totales?.neu)) return (
    <TechPanel style={{ gridColumn: '1 / -1' }}>
      <PanelHeader><PanelTitle>Humor social</PanelTitle></PanelHeader>
      <Empty>
        Todavía no hay comentarios analizados para este tema. El monitoreo de redes
        (snapshot diario, 9 h) captura y clasifica los comentarios de las publicaciones;
        cuando corra, acá vas a ver qué dice la gente.
      </Empty>
    </TechPanel>
  );

  const { totales, serie, posts, voces, temas, emociones, lectura, del_tema: delTema } = data;
  const tot = totales.pos + totales.neg + totales.neu;
  const pct = (n) => (tot ? Math.round((n / tot) * 100) : 0);
  const favPct = pct(totales.pos);
  const colorLectura = favPct >= 50 ? C.pos : favPct >= 30 ? '#ffd166' : C.neg;

  const dona = [
    { name: 'A favor', value: totales.pos, color: C.pos },
    { name: 'En contra', value: totales.neg, color: C.neg },
    { name: 'Neutros', value: totales.neu, color: C.neu },
  ].filter((d) => d.value > 0);

  const maxEmo = Math.max(1, ...emociones.map((e) => e.pct || 0));

  return (
    <TechPanel style={{ gridColumn: '1 / -1' }}>
      <PanelHeader>
        <PanelTitle>Humor social</PanelTitle>
        <TechChip>{totales.comentarios} comentarios · {totales.posts} publicaciones</TechChip>
      </PanelHeader>

      {lectura && <Lectura $color={colorLectura}>{lectura}</Lectura>}
      {!delTema && (
        <ErrorBox style={{ marginBottom: 12 }}>
          No hay publicaciones que mencionen el tema puntual: se muestra el clima general
          de las cuentas monitoreadas.
        </ErrorBox>
      )}

      <Grid>
        {/* Ánimo general */}
        <div>
          <Sub>Cómo reacciona la gente</Sub>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={dona} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                {dona.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <Barra>
            <Seg $c={C.pos} style={{ width: `${pct(totales.pos)}%` }} />
            <Seg $c={C.neu} style={{ width: `${pct(totales.neu)}%` }} />
            <Seg $c={C.neg} style={{ width: `${pct(totales.neg)}%` }} />
          </Barra>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', fontFamily: mono }}>
            <span style={{ color: C.pos }}>{pct(totales.pos)}% a favor</span>
            <span style={{ color: C.neu }}>{pct(totales.neu)}% neutro</span>
            <span style={{ color: C.neg }}>{pct(totales.neg)}% en contra</span>
          </div>
        </div>

        {/* Evolución del ánimo */}
        <div>
          <Sub>Cómo viene día a día</Sub>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={serie} margin={{ top: 6, right: 8, left: -24, bottom: 0 }} stackOffset="expand">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="fecha" tickFormatter={diaCorto} tick={ejeStyle} />
              <YAxis tick={ejeStyle} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={diaCorto} />
              <Area type="monotone" dataKey="pos" stackId="1" stroke={C.pos} fill={C.pos} fillOpacity={0.7} name="a favor" />
              <Area type="monotone" dataKey="neu" stackId="1" stroke={C.neu} fill={C.neu} fillOpacity={0.5} name="neutro" />
              <Area type="monotone" dataKey="neg" stackId="1" stroke={C.neg} fill={C.neg} fillOpacity={0.7} name="en contra" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Temas */}
        {temas.length > 0 && (
          <div>
            <Sub>De qué se habla</Sub>
            {temas.map((t) => (
              <Tema key={t.tema}>
                <TechChip tone={t.lean === 'favor' ? undefined : t.lean === 'contra' ? 'danger' : 'warn'}>
                  {t.lean === 'favor' ? '✓' : t.lean === 'contra' ? '✗' : '~'}
                </TechChip>
                <span className="n">{t.tema}</span>
                <span className="m">{t.menciones}</span>
              </Tema>
            ))}
          </div>
        )}

        {/* Emociones */}
        {emociones.length > 0 && (
          <div>
            <Sub>Con qué emoción</Sub>
            {emociones.map((e) => (
              <EmoBar key={e.emocion}>
                <span className="l">{e.emocion}</span>
                <div className="track"><div className="fill" style={{ width: `${((e.pct || 0) / maxEmo) * 100}%` }} /></div>
                <span className="v">{e.pct}%</span>
              </EmoBar>
            ))}
          </div>
        )}
      </Grid>

      {/* Voces */}
      {(voces.pos.length > 0 || voces.neg.length > 0) && (
        <div style={{ marginTop: 18 }}>
          <Sub>Lo que dice la gente</Sub>
          <Grid>
            {voces.pos.length > 0 && (
              <div>
                <div style={{ color: C.pos, fontSize: '0.8rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaThumbsUp /> A favor de Chouhy
                </div>
                {voces.pos.map((t, i) => <Voz key={i} $c={C.pos}>{t}</Voz>)}
              </div>
            )}
            {voces.neg.length > 0 && (
              <div>
                <div style={{ color: C.neg, fontSize: '0.8rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaThumbsDown /> Críticas
                </div>
                {voces.neg.map((t, i) => <Voz key={i} $c={C.neg}>{t}</Voz>)}
              </div>
            )}
          </Grid>
        </div>
      )}

      {/* Publicaciones del tema */}
      {posts.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <Sub>Publicaciones que más movieron</Sub>
          {posts.map((p, i) => (
            <Post key={p.url || i} href={p.url || undefined} target={p.url ? '_blank' : undefined} rel="noopener noreferrer">
              <div className="cap">{p.caption || '(sin texto)'}</div>
              <div className="meta">
                <span style={{ color: C.pos }}>{p.pos} a favor</span>
                <span style={{ color: C.neg }}>{p.neg} en contra</span>
                <span><FaHeart style={{ fontSize: '0.65rem' }} /> {p.likes ?? '—'}</span>
                <span>{p.n} comentarios</span>
                {p.url && <FaExternalLinkAlt style={{ fontSize: '0.6rem' }} />}
              </div>
            </Post>
          ))}
        </div>
      )}
    </TechPanel>
  );
}
